// src/api/issues.api.ts — SCE Issues
// CRUD des problèmes dans SharePoint SCE_Problemes (via Graph API)
//
// ─── Lot 3.5 — Simplification du formulaire de déclaration ───
//
// À la création, Famille (field_3) et Criticité (field_4) sont systématiquement
// initialisés à la valeur sentinelle 'À qualifier'. Le payload reçu ne les
// contient plus (cf. NewIssuePayload qui les omet).
//
// Le coordinateur peut ensuite les remplacer via updateIssue() depuis la fiche
// détail.
//
// ⚠️ PRÉ-REQUIS SHAREPOINT
//    Les colonnes Choice field_3 (Famille) et field_4 (Criticité) doivent
//    inclure 'À qualifier' (avec À majuscule accentué) dans leur liste de
//    choix — sinon l'écriture est rejetée par SharePoint.
//
// ⚠️ Mapping colonnes SharePoint :
//   Title              → ID custom format "P2026-0042" (calculé après création)
//   field_1            → Titre
//   field_2            → Metier
//   field_3            → Famille (choice — 'À qualifier' à la création)
//   field_4            → Criticite (choice — 'À qualifier' à la création)
//   field_5            → Phase (choice)
//   field_6            → NumeroSerie
//   field_7            → OF
//   field_8            → PN
//   field_9            → FamilleEquipement (choice : Module de pompage /
//                        Module de pompage BioSC / Colonne / Slurry unit)
//   field_10           → Description
//   field_11           → PieceJointe
//   field_13           → DateDeclaration (dateTime)
//   field_14           → Statut (choice)
//   field_15LookupId   → Coordinateur (personOrGroup, MULTI-VALEURS)
//   field_16           → DateCloture
//
// ⚠️ Le déclarant utilise la colonne SYSTÈME `Author` de SharePoint (= "Créé par").

import { graphGet, graphPost, graphPatch } from './graphClient'
import { resolveUserLookupId, resolveUserLookupIds } from './users.api'
import { identifiantsApres, lireAbonnes } from '@/utils/abonnements'
import {
  concerneEDE,
  normaliserSaisieEDE,
  type DocumentationEDE,
  type SaisieEDE,
} from '@/utils/documentationEDE'
import {
  A_QUALIFIER,
  ANNULE,
  basculerBadge,
  CATEGORIES_PAR_SUFFIXE,
  cleRapprochement,
  correspondAuFiltreBadge,
  estActif,
  estTermine,
  commentaireClotureObligatoire,
  erreurBadges,
  estBadgeCloture,
  estMotifAnnulation,
  estReferenceValide,
  estSujet,
  normaliserNumeroPlan,
  ordonnerBadges,
  suffixeNumeroSerie,
  type BadgeAvecReference,
  type BadgeCloture,
  type Issue,
  type IssueFilters,
  type NewIssuePayload,
  type Metier,
  type Criticite,
  type MotifAnnulation,
  type Statut,
  type Phase,
  type CategorieEquipement,
  type Sujet,
} from '@/types/issue.types'
import { coordinateursDuProbleme } from '@/config/sujets.config'
import {
  LIBELLES_CHAMPS,
  analyserModification,
  enTexte,
  saisieDepuis,
  statutReouverture,
  type Changement,
  type ChampModifiable,
  type SaisieModification,
} from '@/utils/modificationIssue'
import { nomComplet } from '@/utils/noms'
import { texteEde, type VersionProbleme } from '@/utils/historiqueChamps'
import { badgesAvecNcs } from '@/utils/declarationNcs'
import { auteurCanonique } from '@/config/comptesAlias.config'
import { colonneSaisiPar } from '@/auth/comptePartage'

// ─── Configuration SharePoint ───

const SITE_ID = import.meta.env.VITE_SHAREPOINT_SITE_ID ?? ''
const LIST_ID = import.meta.env.VITE_LIST_PROBLEMES_ID ?? ''

function listPath(): string {
  return `/sites/${SITE_ID}/lists/${LIST_ID}`
}

const ITEM_SELECT = '$expand=fields&$select=id,createdDateTime,createdBy'

// ─── Colonnes ajoutées après field_16 (Lots 2 et suivants) ───
//
// Contrairement à field_1…field_16 (créées puis renommées), une colonne créée
// aujourd'hui reçoit un nom interne DÉRIVÉ de son nom d'affichage. Créée sous
// le nom `CommentaireCloture` — sans espace ni accent, comme recommandé au
// cadrage — son nom interne est identique. Créée sous « Commentaire de
// clôture », elle s'appellerait `Commentaire_x0020_de_x0020_cl_x00f4_ture`.
//
// Les valeurs ci-dessous supposent la première option. Elles sont regroupées
// ici pour que la confirmation tienne en une ligne par colonne.
//
// Vérification : GET /sites/{SITE_ID}/lists/{LIST_ID}/columns?$select=name,displayName
//
// ⚠️ Une colonne absente de SharePoint fait échouer TOUTE l'écriture qui la
//    vise : sans `CommentaireCloture`, plus aucune clôture n'aboutit. Les
//    colonnes doivent exister AVANT le déploiement de ce code.
const COLONNES_AJOUTEES = {
  commentaireCloture: 'CommentaireCloture', // TODO_SP_COLONNE — texte multiligne, texte brut
  motifAnnulation:    'MotifAnnulation',    // TODO_SP_COLONNE — Choice, SANS valeur par défaut
  dateAnnulation:     'DateAnnulation',     // TODO_SP_COLONNE — Date et heure
  // Lot 3 — valeurs : FNC-T, NC-S, TQC, « Rapport de problème », Autre
  // ⚠️ Choice à SÉLECTION MULTIPLE (confirmé le 24/09) : cf. CLE_TYPE_BADGES.
  badgeCloture:       'BadgeCloture',       // Confirmé — Choice multiple, SANS valeur par défaut
  // Lot 3 bis — références qualité, texte libre au format FNC-T_2026-55
  refFnct:            'RefFNCT',            // TODO_SP_COLONNE — Une ligne de texte
  refNcs:             'RefNCS',             // TODO_SP_COLONNE — Une ligne de texte
  // Fiche v2 — créée le 24/09 : texte multiligne, un plan par ligne. MECA.
  numeroPlan:         'NumeroPlan',
  // Lot 12 — créée le 25/09 : Personne ou groupe, PLUSIEURS valeurs. Écrite
  // par identifiants (`AbonnesLookupId`, cf. CLE_IDS_ABONNES), comme la
  // colonne Coordinateur.
  abonnes:            'Abonnes',
  // Lot 13 — schéma électrique (ELEC / AUTOM), une ligne de texte chacune.
  numeroEDE:          'NumeroEDE',
  revEDE:             'RevEDE',
  pageEDE:            'PageEDE',
  // Lot 14 — créée le 26/09 : une ligne de texte, l'ID de la première fiche
  // du groupe (multi-équipements, option C). Vide pour une fiche seule.
  groupe:             'Groupe',
  // 07/10 — une ligne de texte, SCE_Problemes ET SCE_Commentaires : UPN de qui
  // tenait l'appareil partagé (auth/comptePartage). Écrite à la création,
  // depuis cet appareil seulement.
  saisiPar:           'SaisiPar',
  // 23/10 — une ligne de texte : le sujet (« Goulotte », config/sujets.config).
  // Vide pour la plupart des problèmes.
  sujet:              'Sujet',
} as const

/** Écriture de la colonne Abonnes : identifiants SharePoint + type exigé par Graph. */
const CLE_IDS_ABONNES = `${COLONNES_AJOUTEES.abonnes}LookupId` as const
const CLE_TYPE_ABONNES = `${CLE_IDS_ABONNES}@odata.type` as const

/**
 * Annotation de type exigée par Graph pour écrire une colonne à choix
 * multiple. Sans elle, Graph refuse le tableau ; avec une chaîne simple à la
 * place du tableau, il refuse aussi. Et comme le badge part dans la même
 * requête que le statut, c'est la CLÔTURE ENTIÈRE qui serait rejetée.
 *
 * Même mécanique que `field_15LookupId@odata.type` pour les coordinateurs.
 * Dérivée du nom de colonne pour ne pas diverger si ce nom change.
 */
const CLE_TYPE_BADGES = `${COLONNES_AJOUTEES.badgeCloture}@odata.type` as const

// ─── Format de l'ID custom ───

function buildIssueId(year: number, counter: number): string {
  const pad = String(counter).padStart(4, '0')
  return `P${year}-${pad}`
}

async function getLastCounterForYear(year: number): Promise<number> {
  const prefix = `P${year}-`
  const filter = `startswith(fields/Title,'${escapeODataString(prefix)}')`
  const path =
    `${listPath()}/items?$expand=fields($select=Title)` +
    `&$filter=${encodeURIComponent(filter)}` +
    `&$orderby=fields/Title desc&$top=1`

  const response = await graphGet<SharePointListResponse<{ Title?: string }>>(path)
  const lastTitle = response.value[0]?.fields.Title
  if (!lastTitle) return 0

  const match = lastTitle.match(/-(\d+)$/)
  if (!match) return 0
  return parseInt(match[1] ?? '0', 10)
}

// ─── Structure brute Graph API ───

interface IssueFields {
  Title?: string
  field_1?: string
  field_2?: Metier
  field_3?: string
  field_4?: Criticite
  field_5?: Phase
  field_6?: string
  field_7?: string
  field_8?: string
  field_9?: CategorieEquipement
  field_10?: string
  field_11?: string
  field_13?: string
  field_14?: Statut
  field_15LookupId?: number[]
  'field_15LookupId@odata.type'?: 'Collection(Edm.Int32)'
  // null : effacement (réouverture, Lot 7)
  field_16?: string | null
  // Lots 2+ — noms internes centralisés dans COLONNES_AJOUTEES
  [COLONNES_AJOUTEES.commentaireCloture]?: string
  [COLONNES_AJOUTEES.motifAnnulation]?: string | null
  [COLONNES_AJOUTEES.dateAnnulation]?: string | null
  // Choix multiple : Graph renvoie un tableau. Une chaîne reste acceptée en
  // lecture, au cas où la colonne repasserait un jour en choix simple.
  [COLONNES_AJOUTEES.badgeCloture]?: string[] | string
  [CLE_TYPE_BADGES]?: 'Collection(Edm.String)'
  [COLONNES_AJOUTEES.refFnct]?: string
  [COLONNES_AJOUTEES.refNcs]?: string
  [COLONNES_AJOUTEES.numeroPlan]?: string
  [COLONNES_AJOUTEES.numeroEDE]?: string
  [COLONNES_AJOUTEES.revEDE]?: string
  [COLONNES_AJOUTEES.pageEDE]?: string
  [COLONNES_AJOUTEES.groupe]?: string
  [COLONNES_AJOUTEES.saisiPar]?: string
  [COLONNES_AJOUTEES.sujet]?: string
  // Lot 12 — lecture : forme renvoyée par Graph, tolérée telle quelle
  // (utils/abonnements.ts) ; écriture : identifiants.
  [COLONNES_AJOUTEES.abonnes]?: unknown
  [CLE_IDS_ABONNES]?: unknown
  [CLE_TYPE_ABONNES]?: 'Collection(Edm.Int32)'
}

/**
 * Lit les badges tels que SharePoint les renvoie, et n'en garde que les
 * valeurs connues, dans l'ordre canonique.
 *
 * Tolérante par construction : tableau (choix multiple), chaîne (choix
 * simple), vide ou absent. Une valeur saisie à la main dans SharePoint, hors
 * liste, est écartée plutôt que de se faire passer pour un badge typé.
 */
function lireBadges(brut: string[] | string | undefined): BadgeCloture[] | undefined {
  const valeurs = Array.isArray(brut) ? brut : brut ? [brut] : []
  const badges = ordonnerBadges(valeurs.filter(estBadgeCloture))
  return badges.length > 0 ? badges : undefined
}

interface GraphIdentity {
  user?: {
    displayName?: string
    email?: string
    id?: string
  }
}

interface SharePointListItem<TFields> {
  id: string
  createdDateTime?: string
  fields: TFields & { Title?: string }
  createdBy?: GraphIdentity
}

interface SharePointListResponse<T> {
  value: SharePointListItem<T>[]
  '@odata.nextLink'?: string
}

// ─── Mapper SharePoint → Issue ───
//
// ⚠️ Pour les anciens problèmes créés AVANT le Lot 3.5, field_3 et field_4
// peuvent être vides (chaîne vide). On les remappe sur 'À qualifier' pour que
// l'UI ait toujours une valeur cohérente à afficher.

function mapItemToIssue(item: SharePointListItem<IssueFields>): Issue {
  const f = item.fields
  // Ramené à la personne qu'il représente s'il s'agit d'un compte secondaire
  // (config/comptesAlias.config) : tout l'aval voit le vrai déclarant.
  // Appareil partagé (iPad de l'atelier) : la colonne SaisiPar dit qui le tenait.
  const auteur = auteurCanonique(item.createdBy?.user, undefined, undefined, f[COLONNES_AJOUTEES.saisiPar])

  const numeroPlan = normaliserNumeroPlan(f[COLONNES_AJOUTEES.numeroPlan] ?? '')
  return {
    id:               f.Title ?? `#${item.id}`,
    titre:            f.field_1 ?? '',
    metier:           f.field_2 ?? 'ELEC',
    // Valeur connue seulement : un texte saisi à la main ne se fait pas passer pour un sujet.
    ...((): { sujet?: Sujet } => {
      const s = f[COLONNES_AJOUTEES.sujet]?.trim()
      return estSujet(s) ? { sujet: s } : {}
    })(),
    famille:          f.field_3 && f.field_3 !== '' ? f.field_3 : A_QUALIFIER,
    criticite:        (f.field_4 && f.field_4 !== ('' as Criticite))
                        ? f.field_4
                        : A_QUALIFIER,
    phase:            f.field_5 ?? 'Fabrication',
    numeroSerie:      f.field_6 ?? '',
    of:               f.field_7 ?? '',
    pn:               f.field_8 ?? '',
    // Pas de valeur par defaut arbitraire : si field_9 est vide (ligne creee
    // hors application), on rededuit la categorie depuis le N de serie plutot
    // que d'etiqueter au hasard une colonne en module de pompage.
    familleEquipement:
      f.field_9 ??
      CATEGORIES_PAR_SUFFIXE[suffixeNumeroSerie(f.field_6 ?? '')] ??
      'Flowdrive',
    description:      f.field_10 ?? '',
    ...(f.field_11 ? { pieceJointe: f.field_11 } : {}),
    declarant:        auteur?.email ?? '',
    declarantNom:     nomComplet(auteur?.displayName ?? auteur?.email ?? '', auteur?.email),
    dateDeclaration:  f.field_13 ?? item.createdDateTime ?? '',
    statut:           f.field_14 ?? 'Ouvert',
    ...(f.field_16 ? { dateCloture: f.field_16 } : {}),
    // Lot 2. Lecture tolérante : une colonne absente ou vide donne `undefined`,
    // jamais une erreur — les problèmes antérieurs au Lot 2 n'ont rien ici.
    ...(f[COLONNES_AJOUTEES.commentaireCloture]
      ? { commentaireCloture: f[COLONNES_AJOUTEES.commentaireCloture] }
      : {}),
    // Motif validé contre la liste connue : une valeur saisie à la main dans
    // SharePoint, hors liste, ne doit pas se faire passer pour un motif typé.
    ...(estMotifAnnulation(f[COLONNES_AJOUTEES.motifAnnulation])
      ? { motifAnnulation: f[COLONNES_AJOUTEES.motifAnnulation] as MotifAnnulation }
      : {}),
    ...(f[COLONNES_AJOUTEES.dateAnnulation]
      ? { dateAnnulation: f[COLONNES_AJOUTEES.dateAnnulation] as string }
      : {}),
    // Lot 3. Même prudence que le motif : seules les valeurs connues passent.
    ...((): { badgesCloture?: BadgeCloture[] } => {
      const badges = lireBadges(f[COLONNES_AJOUTEES.badgeCloture])
      return badges ? { badgesCloture: badges } : {}
    })(),
    // Lot 3 bis. Lues TELLES QUELLES, sans validation de format : c'est un
    // texte libre, et une référence corrigée à la main dans SharePoint
    // (« FNC-T 2026-55 ») doit rester visible plutôt que disparaître — pour
    // un enregistrement qualité, masquer vaut perdre.
    ...(f[COLONNES_AJOUTEES.refFnct]?.trim()
      ? { refFnct: f[COLONNES_AJOUTEES.refFnct]?.trim() }
      : {}),
    ...(f[COLONNES_AJOUTEES.refNcs]?.trim()
      ? { refNcs: f[COLONNES_AJOUTEES.refNcs]?.trim() }
      : {}),
    ...(numeroPlan ? { numeroPlan } : {}),
    abonnes: lireAbonnes(f[COLONNES_AJOUTEES.abonnes], f[CLE_IDS_ABONNES]),
    // Lot 13 — lues telles quelles (corrigées à la main dans SharePoint, elles
    // doivent rester visibles, comme les références qualité).
    ...(f[COLONNES_AJOUTEES.numeroEDE]?.trim() ? { numeroEDE: f[COLONNES_AJOUTEES.numeroEDE]?.trim() } : {}),
    ...(f[COLONNES_AJOUTEES.revEDE]?.trim() ? { revEDE: f[COLONNES_AJOUTEES.revEDE]?.trim() } : {}),
    ...(f[COLONNES_AJOUTEES.pageEDE]?.trim() ? { pageEDE: f[COLONNES_AJOUTEES.pageEDE]?.trim() } : {}),
    ...(f[COLONNES_AJOUTEES.groupe]?.trim() ? { groupe: f[COLONNES_AJOUTEES.groupe]?.trim() } : {}),
  }
}

// ─── Mapper NewIssuePayload → champs SharePoint ───
//
// Lot 3.5 — Famille et Criticité ne sont plus dans le payload : on les force
// systématiquement à 'À qualifier'.

async function mapPayloadToFields(
  payload: NewIssuePayload,
  coordinateurUPNs?: string[],
): Promise<Partial<IssueFields>> {
  const fields: Partial<IssueFields> = {
    field_1:  payload.titre,
    field_2:  payload.metier,
    ...(payload.sujet ? { [COLONNES_AJOUTEES.sujet]: payload.sujet } : {}),
    field_3:  A_QUALIFIER,            // ← initialisé à 'À qualifier'
    field_4:  A_QUALIFIER,            // ← initialisé à 'À qualifier'
    field_5:  payload.phase,
    field_6:  payload.numeroSerie,
    field_7:  payload.of,
    field_8:  payload.pn,
    field_9:  payload.familleEquipement,
    field_10: payload.description,
    ...(payload.pieceJointe ? { field_11: payload.pieceJointe } : {}),
    field_13: new Date().toISOString(),
    field_14: 'Ouvert' as Statut,
    // Lot 14 : fiche sœur d'un groupe existant. La première fiche d'un groupe
    // reçoit le sien avec son ID (cf. createIssue, `ouvreGroupe`).
    ...(payload.groupe ? { [COLONNES_AJOUTEES.groupe]: payload.groupe } : {}),
    // Documentation connue à la déclaration (15/10) : seulement si renseignée.
    ...(payload.numeroPlan ? { [COLONNES_AJOUTEES.numeroPlan]: payload.numeroPlan } : {}),
    ...(payload.numeroEDE
      ? {
          [COLONNES_AJOUTEES.numeroEDE]: payload.numeroEDE,
          [COLONNES_AJOUTEES.revEDE]: payload.revEDE ?? '',
          ...(payload.pageEDE ? { [COLONNES_AJOUTEES.pageEDE]: payload.pageEDE } : {}),
        }
      : {}),
  }

  // Coordinateurs : résolution UPN[] → LookupId[] en parallèle.
  if (coordinateurUPNs && coordinateurUPNs.length > 0) {
    try {
      const lookups = await resolveUserLookupIds(coordinateurUPNs)
      const validIds = [...lookups.values()].filter(
        (id): id is number => id !== null,
      )

      if (validIds.length > 0) {
        fields['field_15LookupId@odata.type'] = 'Collection(Edm.Int32)'
        fields.field_15LookupId = validIds
      } else {
        // eslint-disable-next-line no-console
        console.warn(
          '[issues.api] Aucun coordinateur n\'a pu être résolu — fiche créée sans coordinateur:',
          coordinateurUPNs,
        )
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        '[issues.api] Résolution multi-coordinateurs a échoué — fiche créée sans coordinateur:',
        err,
      )
    }
  }

  return fields
}

// ─── Filtres OData ───

/**
 * Construit la clause `$filter` OData.
 *
 * ─── Ce qui part au serveur, et ce qui reste au client ───
 *
 * Seules les colonnes de VOCABULAIRE CONTRÔLÉ sont filtrées côté serveur :
 * métier, criticité, statut, catégorie. Ce sont des colonnes Choice, écrites
 * par l'application, aux valeurs finies et sans ambiguïté de graphie — et
 * elles sont INDEXÉES dans SharePoint, ce qui est la condition pour que Graph
 * accepte le filtre.
 *
 * Les identifiants Windchill (N° de série, OF, PN) n'y sont PLUS, pour deux
 * raisons qui pointent dans le même sens :
 *
 *  1. Ils ne sont pas indexés, et Graph répond `400 Bad Request` sur un
 *     `$filter` visant une colonne non indexée — sans exécuter la requête.
 *     C'est ce qui vidait la fiche équipement (relevé du 18/09/2026 :
 *     `$filter=fields/field_6 eq '4844-SC'` → 400).
 *  2. Même indexées, l'égalité OData exacte serait le mauvais outil : ces
 *     valeurs ne sont pas normalisées (cf. `cleRapprochement`). C'est le
 *     constat qui avait déjà fait sortir le PN du filtre le 16/09.
 *
 * Les indexer aurait donc réparé le symptôme (1) en laissant la cause (2)
 * intacte, prête à produire des listes vides sans le moindre message.
 *
 * ⚠️ N'ajouter ici que des colonnes Choice ET indexées. Toute autre colonne
 *    se filtre côté client, dans `getIssues`.
 */
function buildFilterQuery(filters?: IssueFilters): string {
  if (!filters) return ''
  const clauses: string[] = []

  if (filters.metier?.length) {
    const or = filters.metier.map((m: Metier) => `fields/field_2 eq '${m}'`).join(' or ')
    clauses.push(`(${or})`)
  }
  if (filters.criticite?.length) {
    const or = filters.criticite
      .map((c: Criticite) => `fields/field_4 eq '${escapeODataString(c)}'`)
      .join(' or ')
    clauses.push(`(${or})`)
  }
  if (filters.statut?.length) {
    const or = filters.statut.map((s: Statut) => `fields/field_14 eq '${s}'`).join(' or ')
    clauses.push(`(${or})`)
  }
  if (filters.familleEquipement) {
    clauses.push(`fields/field_9 eq '${filters.familleEquipement}'`)
  }
  // Note : `numeroSerie`, `of`, `pn`, `declarant`, `recherche` et `aQualifier`
  // sont appliqués côté client (cf. getIssues).

  return clauses.length > 0
    ? `&$filter=${encodeURIComponent(clauses.join(' and '))}`
    : ''
}

function escapeODataString(value: string): string {
  return value.replace(/'/g, "''")
}

// ─── API publique ───

export async function getIssues(filters?: IssueFilters): Promise<Issue[]> {
  const filterQuery = buildFilterQuery(filters)
  let path: string | null =
    `${listPath()}/items?${ITEM_SELECT}` +
    `&$orderby=fields/field_13 desc&$top=200${filterQuery}`

  // Pagination : $top=200 est la taille de PAGE, pas une limite de resultats.
  // Sans cette boucle, la liste etait silencieusement tronquee au 201e
  // probleme — pas d'erreur, juste un dashboard faux. Le nextLink rejoue le
  // filtre et le tri de la requete initiale, rien a reconstruire.
  const items: SharePointListItem<IssueFields>[] = []
  while (path) {
    const response: SharePointListResponse<IssueFields> =
      await graphGet<SharePointListResponse<IssueFields>>(path)
    items.push(...response.value)
    path = response['@odata.nextLink'] ?? null
  }

  let issues = items.map(mapItemToIssue)

  // ─── Identifiants Windchill : rapprochement client, normalisé ───
  //
  // Ces trois filtres ne partent PLUS en OData (cf. buildFilterQuery). Ils
  // sont appliqués ici sur une clé normalisée, ce qui règle d'un coup le
  // rejet Graph (colonne non indexée) et l'écart de graphie entre le
  // référentiel et les déclarations.
  if (filters?.numeroSerie) {
    const cible = cleRapprochement(filters.numeroSerie)
    issues = issues.filter((i) => cleRapprochement(i.numeroSerie) === cible)
  }
  if (filters?.of) {
    const cible = cleRapprochement(filters.of)
    issues = issues.filter((i) => cleRapprochement(i.of) === cible)
  }
  if (filters?.pn) {
    const cible = cleRapprochement(filters.pn)
    issues = issues.filter((i) => cleRapprochement(i.pn) === cible)
  }

  // Filtre déclarant côté client (comparaison sur l'e-mail du créateur).
  //
  // Plusieurs identités possibles pour une même personne — cf.
  // utils/identiteDeclarant : un guest B2B est stocké dans Author sous son
  // e-mail externe, jamais sous son UPN `#EXT#`.
  //
  // ⚠️ Un tableau VIDE ne filtre rien du tout, il exclut tout. C'est
  // volontaire : ce filtre est le cloisonnement des sous-traitants, et son
  // échec doit masquer des fiches, jamais en révéler.
  if (filters?.declarant) {
    const cibles = new Set(
      (Array.isArray(filters.declarant) ? filters.declarant : [filters.declarant])
        .map((v) => v.trim().toLowerCase()),
    )
    issues = issues.filter((i) => cibles.has((i.declarant ?? '').trim().toLowerCase()))
  }

  // Filtre "à qualifier" côté client : un problème est à qualifier si
  // famille OU criticité valent encore 'À qualifier' ET qu'il est encore
  // actif. Un problème annulé garde volontairement ses sentinelles (il n'a
  // pas été qualifié, il a été écarté) : sans la garde `estActif`, il
  // remonterait indéfiniment comme du travail en attente.
  if (filters?.aQualifier === true) {
    issues = issues.filter(
      (i) => estActif(i) && (i.famille === A_QUALIFIER || i.criticite === A_QUALIFIER),
    )
  } else if (filters?.aQualifier === false) {
    issues = issues.filter(
      (i) => i.famille !== A_QUALIFIER && i.criticite !== A_QUALIFIER,
    )
  }

  // Recherche texte libre côté client
  if (filters?.recherche) {
    const q = filters.recherche.toLowerCase()
    issues = issues.filter(
      (i) =>
        i.titre.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.id.toLowerCase().includes(q),
    )
  }

  // Badge de clôture (Lot 3) — côté client, colonne non indexée.
  if (filters?.badgeCloture?.length) {
    const voulus = new Set(filters.badgeCloture)
    issues = issues.filter((i) => correspondAuFiltreBadge(i, voulus))
  }

  return issues
}

export async function getIssueById(
  id: string,
): Promise<{ issue: Issue; itemId: string } | null> {
  const filter = `fields/Title eq '${escapeODataString(id)}'`
  const path =
    `${listPath()}/items?${ITEM_SELECT}` +
    `&$filter=${encodeURIComponent(filter)}&$top=1`

  const response = await graphGet<SharePointListResponse<IssueFields>>(path)
  const item = response.value[0]
  if (!item) return null
  return { issue: mapItemToIssue(item), itemId: item.id }
}

// ─── Suivre un problème (Lot 12) ───

/**
 * Abonne ou désabonne une personne (cloche « Suivre » de la fiche).
 *
 * Relecture avant écriture : la colonne porte TOUTE la liste des abonnés, et
 * l'écrire depuis l'état de la fiche effacerait quiconque s'est abonné
 * pendant qu'elle était ouverte. On relit donc la ligne, on ajoute ou retire
 * la seule personne concernée, et on réécrit — une requête, pas d'événement
 * dans l'activité (suivre n'est pas un acte sur le problème). La fiche se
 * relit ensuite, pour les noms.
 */
/**
 * Suivre ou ne plus suivre un problème connu par son N° (P2026-0042) : à la
 * déclaration (abonnement d'office, 29/10) et depuis « Mes notifications », où
 * l'identifiant SharePoint de la ligne n'est pas sous la main.
 */
export async function suivreIssueParId(id: string, upn: string, suivre: boolean): Promise<void> {
  const trouve = await getIssueById(id)
  if (!trouve) throw new Error(`Problème ${id} introuvable : abonnement non enregistré.`)
  await suivreIssue(trouve.itemId, upn, suivre)
}

export async function suivreIssue(itemId: string, upn: string, suivre: boolean): Promise<void> {
  const monId = await resolveUserLookupId(upn)
  if (monId === null) {
    throw new Error('Impossible de vous identifier dans SharePoint : abonnement non enregistré.')
  }
  const relu = await graphGet<SharePointListItem<IssueFields>>(`${listPath()}/items/${itemId}?${ITEM_SELECT}`)
  const actuels = lireAbonnes(relu.fields[COLONNES_AJOUTEES.abonnes], relu.fields[CLE_IDS_ABONNES])
  const deja = actuels.some((a) => a.lookupId === monId)
  if (deja === suivre) return // déjà dans l'état voulu (autre onglet, double clic)
  await graphPatch<unknown>(`${listPath()}/items/${itemId}/fields`, {
    [CLE_TYPE_ABONNES]: 'Collection(Edm.Int32)',
    [CLE_IDS_ABONNES]: identifiantsApres(actuels, monId, suivre),
  })
}

/**
 * Crée un problème en 2 temps :
 *   1. POST sans Title → SharePoint génère un id interne et alimente Author auto
 *   2. PATCH du Title avec le format custom "P2026-0042"
 *
 * Famille et Criticité sont systématiquement initialisés à 'À qualifier'
 * (cf. mapPayloadToFields).
 *
 * @param coordinateurUPNs Tableau d'UPN à résoudre et assigner au champ
 *                         multi-valeurs Coordinateur. Les échecs de résolution
 *                         sont ignorés (best effort).
 * @param options.ouvreGroupe première fiche d'un groupe multi-équipements
 *                         (Lot 14) : sa colonne Groupe reçoit son propre ID,
 *                         DANS LA MÊME requête que le Title. Le flow 1, qui
 *                         relit la fiche pour trouver son numéro, voit donc
 *                         aussi qu'elle ouvre un groupe.
 */
export async function createIssue(
  payload: NewIssuePayload,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  _declarant: { upn: string; nom: string },
  coordinateurUPNs?: string[],
  options: { ouvreGroupe?: boolean } = {},
): Promise<Issue> {
  const fields = { ...(await mapPayloadToFields(payload, coordinateurUPNs)), ...colonneSaisiPar() }

  const created = await graphPost<SharePointListItem<IssueFields>>(
    `${listPath()}/items`,
    { fields },
  )

  const year = new Date().getFullYear()
  const lastCounter = await getLastCounterForYear(year)
  const customId = buildIssueId(year, lastCounter + 1)

  await graphPatch<unknown>(
    `${listPath()}/items/${created.id}/fields`,
    options.ouvreGroupe ? { Title: customId, [COLONNES_AJOUTEES.groupe]: customId } : { Title: customId },
  )

  const full = await graphGet<SharePointListItem<IssueFields>>(
    `${listPath()}/items/${created.id}?${ITEM_SELECT}`,
  )
  return mapItemToIssue(full)
}

export async function updateIssue(
  itemId: string,
  partial: Partial<Issue>,
): Promise<Issue> {
  const fields = versChampsSharePoint(partial)

  if (partial.coordinateur !== undefined) {
    try {
      const lookupId = await resolveUserLookupId(partial.coordinateur)
      if (lookupId !== null) {
        fields['field_15LookupId@odata.type'] = 'Collection(Edm.Int32)'
        fields.field_15LookupId = [lookupId]
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn(
        `[issues.api] Résolution coordinateur ${partial.coordinateur} a échoué:`,
        err,
      )
    }
  }

  return ecrireChamps(itemId, fields)
}

/** PATCH des champs puis relecture : l'appelant reçoit le problème tel que stocké. */
async function ecrireChamps(itemId: string, fields: Partial<IssueFields>): Promise<Issue> {
  await graphPatch<unknown>(
    `${listPath()}/items/${itemId}/fields`,
    fields,
  )

  const full = await graphGet<SharePointListItem<IssueFields>>(
    `${listPath()}/items/${itemId}?${ITEM_SELECT}`,
  )
  return mapItemToIssue(full)
}

/**
 * Traduit un problème partiel en colonnes SharePoint — le coordinateur
 * excepté, qui demande une résolution réseau (UPN → LookupId).
 */
function versChampsSharePoint(partial: Partial<Issue>): Partial<IssueFields> {
  const fields: Partial<IssueFields> = {}

  if (partial.titre !== undefined)            fields.field_1  = partial.titre
  if (partial.metier !== undefined)           fields.field_2  = partial.metier
  if (partial.famille !== undefined)          fields.field_3  = partial.famille
  if (partial.criticite !== undefined)        fields.field_4  = partial.criticite
  if (partial.phase !== undefined)            fields.field_5  = partial.phase
  if (partial.numeroSerie !== undefined)      fields.field_6  = partial.numeroSerie
  if (partial.of !== undefined)               fields.field_7  = partial.of
  if (partial.pn !== undefined)               fields.field_8  = partial.pn
  if (partial.familleEquipement !== undefined) fields.field_9 = partial.familleEquipement
  if (partial.description !== undefined)      fields.field_10 = partial.description
  if (partial.pieceJointe !== undefined)      fields.field_11 = partial.pieceJointe
  if (partial.statut !== undefined)           fields.field_14 = partial.statut
  if (partial.dateCloture !== undefined)      fields.field_16 = partial.dateCloture
  // ⚠️ Jusqu'au Lot 2, commentaireCloture n'avait AUCUNE ligne ici : la fiche
  //    le saisissait, l'API le jetait sans erreur. Les trois champs ci-dessous
  //    doivent exister dans SharePoint (cf. COLONNES_AJOUTEES).
  if (partial.commentaireCloture !== undefined) {
    fields[COLONNES_AJOUTEES.commentaireCloture] = partial.commentaireCloture
  }
  if (partial.motifAnnulation !== undefined) {
    fields[COLONNES_AJOUTEES.motifAnnulation] = partial.motifAnnulation
  }
  if (partial.dateAnnulation !== undefined) {
    fields[COLONNES_AJOUTEES.dateAnnulation] = partial.dateAnnulation
  }
  if (partial.badgesCloture !== undefined) {
    // Tableau + annotation de type : format imposé par une colonne à choix
    // multiple (cf. CLE_TYPE_BADGES).
    fields[CLE_TYPE_BADGES] = 'Collection(Edm.String)'
    fields[COLONNES_AJOUTEES.badgeCloture] = ordonnerBadges(partial.badgesCloture)
  }
  // Chaîne vide = effacer la référence (colonne texte).
  if (partial.refFnct !== undefined) fields[COLONNES_AJOUTEES.refFnct] = partial.refFnct
  if (partial.refNcs !== undefined)  fields[COLONNES_AJOUTEES.refNcs] = partial.refNcs
  if (partial.numeroPlan !== undefined) fields[COLONNES_AJOUTEES.numeroPlan] = partial.numeroPlan
  if (partial.numeroEDE !== undefined) fields[COLONNES_AJOUTEES.numeroEDE] = partial.numeroEDE
  if (partial.revEDE !== undefined)    fields[COLONNES_AJOUTEES.revEDE] = partial.revEDE
  if (partial.pageEDE !== undefined)   fields[COLONNES_AJOUTEES.pageEDE] = partial.pageEDE
  if (partial.groupe !== undefined)    fields[COLONNES_AJOUTEES.groupe] = partial.groupe
  // '' (modifierIssue) : sujet retiré, colonne vidée.
  if (partial.sujet !== undefined)     fields[COLONNES_AJOUTEES.sujet] = partial.sujet

  return fields
}

// ─── Cycle de vie (Lot 2) ───
//
// Les trois transitions d'un problème passent par ces fonctions, jamais par
// un `updateIssue({ statut })` écrit à la main dans une page. Les règles
// métier — ce qui est obligatoire, quel statut suit quel autre — vivent donc
// ici, une seule fois, et aucun futur écran ne peut les contourner.
//
//   qualifyIssue   Ouvert   → En cours   famille + criticité
//   annulerIssue   actif    → Annulé     motif + commentaire obligatoires
//   cloturerIssue  actif    → Clos       commentaire obligatoire
//
// preparerTraitementQualite (30/09) écrit badges, références et commentaire
// sur un problème ACTIF sans changer son statut : la clôture les reprend.
//
// Chacune écrit en UNE seule requête : pas d'état intermédiaire possible où
// le statut aurait changé sans son motif ou son commentaire.

/**
 * Qualification par le coordinateur : famille + criticité, et passage
 * automatique de `Ouvert` à `En cours`.
 *
 * C'est la qualification qui fait entrer un problème en traitement : elle
 * remplace la fonction « Prise en charge », retirée à la demande des
 * coordinateurs (réunion du 18/09/2026).
 *
 * Un problème déjà `En cours` — pris en charge avant le Lot 2, puis resté
 * à qualifier — garde son statut : seule la famille et la criticité changent.
 *
 * @returns le problème à jour, et le statut quitté si une transition a eu lieu
 *          (pour tracer `Ouvert → En cours` dans la timeline).
 * @throws Error si famille ou criticité vaut encore 'À qualifier'
 */
export async function qualifyIssue(
  itemId: string,
  qualification: { famille: string; criticite: Criticite },
  statutActuel: Statut,
): Promise<{ issue: Issue; statutQuitte: Statut | null }> {
  if (
    qualification.famille === A_QUALIFIER ||
    qualification.criticite === A_QUALIFIER
  ) {
    throw new Error(
      'La qualification doit fournir une famille et une criticité définitives ' +
      "(ni l'une ni l'autre ne peut rester 'À qualifier').",
    )
  }
  if (!estActif({ statut: statutActuel })) {
    throw new Error(`Un problème ${statutActuel.toLowerCase()} ne se qualifie plus.`)
  }

  const passeEnCours = statutActuel === 'Ouvert'
  const issue = await updateIssue(itemId, {
    famille: qualification.famille,
    criticite: qualification.criticite,
    ...(passeEnCours ? { statut: 'En cours' as const } : {}),
  })
  return { issue, statutQuitte: passeEnCours ? statutActuel : null }
}

/**
 * Annulation d'un problème qui n'en est pas un — au moment de la qualification.
 *
 * Famille et criticité restent à 'À qualifier' : le problème n'a pas été
 * qualifié, il a été écarté. Le lui attribuer une criticité fausserait les
 * statistiques.
 *
 * @throws Error si le motif est inconnu, le commentaire vide, ou le problème
 *         déjà terminé. Contrôles doublés côté API : le bouton désactivé de la
 *         fiche est une commodité, pas une garantie.
 */
export async function annulerIssue(
  itemId: string,
  annulation: { motif: MotifAnnulation; commentaire: string },
  statutActuel: Statut,
): Promise<Issue> {
  if (!estMotifAnnulation(annulation.motif)) {
    throw new Error(`Motif d'annulation inconnu : « ${String(annulation.motif)} ».`)
  }
  const commentaire = annulation.commentaire.trim()
  if (!commentaire) {
    throw new Error("Un commentaire est obligatoire pour annuler un problème.")
  }
  if (!estActif({ statut: statutActuel })) {
    throw new Error(`Un problème ${statutActuel.toLowerCase()} ne s'annule plus.`)
  }

  return updateIssue(itemId, {
    statut: ANNULE,
    motifAnnulation: annulation.motif,
    dateAnnulation: new Date().toISOString(),
    // Stocké SUR le problème, dans la même requête que le statut — pas
    // seulement dans la timeline. L'écriture de l'événement de timeline est
    // best-effort : si elle échouait (valeur de choix manquante dans
    // SCE_Commentaires, par exemple), l'annulation aboutirait sans que sa
    // justification survive nulle part. La colonne est partagée avec la
    // clôture : c'est le commentaire de FIN, le statut dit lequel.
    commentaireCloture: commentaire,
    // Un traitement qualité préparé avant (preparerTraitementQualite) ne
    // survit pas : un problème écarté n'ouvre ni FNC-T ni NC-S.
    badgesCloture: [],
    refFnct: '',
    refNcs: '',
  })
}

/**
 * Clôture d'un problème traité.
 *
 * Deux obligations, pour tous les métiers :
 *  - au moins un badge de clôture (Lot 3), qui dit le ou les traitements
 *    qualité retenus. `Autre` existe pour les cas hors circuit qualité — il
 *    n'y a donc jamais de raison de clore sans badge (décision 4 du cadrage) ;
 *  - un commentaire, mais seulement avec le badge « Autre » (02/10) : les
 *    autres badges disent déjà comment le problème a été traité.
 *
 * @throws Error si la sélection de badges est vide, contient un badge
 *         inconnu ou un doublon, combine « Autre » à un autre badge ; si le
 *         commentaire est vide avec « Autre » ; si le problème est déjà
 *         terminé — dans tous les cas sans rien écrire.
 */
export async function cloturerIssue(
  itemId: string,
  cloture: {
    commentaire: string
    badges: readonly BadgeCloture[]
    /** Facultatives (Lot 3 bis) — peuvent être renseignées après coup. */
    references?: ReferencesQualite
  },
  statutActuel: Statut,
): Promise<Issue> {
  const refusBadges = erreurBadges(cloture.badges)
  if (refusBadges) throw new Error(refusBadges)
  const commentaire = cloture.commentaire.trim()
  if (!commentaire && commentaireClotureObligatoire(cloture.badges)) {
    throw new Error('Un commentaire de clôture est obligatoire avec le badge « Autre ».')
  }
  if (!estActif({ statut: statutActuel })) {
    throw new Error(`Un problème ${statutActuel.toLowerCase()} ne se clôt plus.`)
  }
  const refs = validerReferences(cloture.references ?? {}, cloture.badges)

  return updateIssue(itemId, {
    statut: 'Clos',
    dateCloture: new Date().toISOString(),
    commentaireCloture: commentaire,
    badgesCloture: [...cloture.badges],
    // Toujours écrites, vides comprises : un traitement préparé avant la
    // clôture (preparerTraitementQualite) a pu laisser une référence que la
    // clôture retire, avec son badge ou sans.
    refFnct: refs.fnct ?? '',
    refNcs: refs.ncs ?? '',
  })
}

// ─── Références qualité (Lot 3 bis) ───

/**
 * Références à écrire. `undefined` = ne pas toucher ; chaîne vide = effacer
 * (seulement pertinent après coup, sur un problème déjà clos).
 */
export interface ReferencesQualite {
  fnct?: string
  ncs?: string
}

/**
 * Contrôle d'API, doublé de celui de l'interface :
 *  - une référence n'a de sens que si son badge est retenu — une FNC-T
 *    renseignée sur un problème badgé TQC seul serait une donnée orpheline ;
 *  - une référence non vide doit respecter le format de l'AQ, à la lettre :
 *    c'est ce qui permettra de retrouver le document automatiquement.
 *
 * @throws Error au premier écart, sans rien écrire.
 */
function validerReferences(
  refs: ReferencesQualite,
  badges: readonly BadgeCloture[],
): ReferencesQualite {
  const controler = (badge: BadgeAvecReference, ref: string | undefined): void => {
    if (ref === undefined || ref === '') return
    if (!badges.includes(badge)) {
      throw new Error(`Une référence ${badge} exige le badge ${badge}.`)
    }
    if (!estReferenceValide(badge, ref)) {
      throw new Error(`Référence ${badge} invalide : « ${ref} » (attendu : ${badge}_2026-55).`)
    }
  }
  controler('FNC-T', refs.fnct)
  controler('NC-S', refs.ncs)
  return refs
}

/**
 * Renseigne, corrige ou efface les références qualité d'un problème DÉJÀ
 * CLOS — le numéro de FNC-T ou de NC-S n'existe pas toujours au moment de la
 * clôture (arbitrage du 24/09/2026).
 *
 * Seules les références fournies sont écrites ; les autres restent intactes.
 * La trace dans la timeline est à la charge de l'appelant, comme pour les
 * autres transitions (événement `Modification`).
 *
 * @throws Error si le problème n'est pas clos, ou si une référence est
 *         invalide ou orpheline de son badge — sans rien écrire.
 */
export async function renseignerReferences(
  itemId: string,
  refs: ReferencesQualite,
  issue: Pick<Issue, 'statut' | 'badgesCloture'>,
): Promise<Issue> {
  if (issue.statut !== 'Clos') {
    throw new Error('Les références qualité se renseignent sur un problème clos.')
  }
  validerReferences(refs, issue.badgesCloture ?? [])
  if (refs.fnct === undefined && refs.ncs === undefined) {
    throw new Error('Aucune référence à enregistrer.')
  }
  return updateIssue(itemId, {
    ...(refs.fnct !== undefined ? { refFnct: refs.fnct } : {}),
    ...(refs.ncs !== undefined ? { refNcs: refs.ncs } : {}),
  })
}

/**
 * Traitement qualité préparé AVANT la clôture (30/09) : on sait déjà qu'une
 * NC-S sera ouverte, mais le problème n'est pas encore clos.
 *
 * Mêmes colonnes que la clôture (badges, références, commentaire de fin),
 * statut inchangé : `cloturerIssue` les reprend et les confirme, la
 * réouverture les efface comme d'habitude. Les statistiques de badges ne
 * comptent que les clos : un traitement prévu n'y entre pas.
 *
 * Remplacement complet, pas un ajout : une référence dont le badge est décoché
 * est effacée, et tout vide retire le traitement prévu.
 *
 * @throws Error si le problème n'est plus actif, si la sélection de badges
 *         est incohérente ou si une référence est invalide — sans rien écrire.
 */
export async function preparerTraitementQualite(
  itemId: string,
  traitement: {
    badges: readonly BadgeCloture[]
    references?: ReferencesQualite
    commentaire: string
  },
  statutActuel: Statut,
): Promise<Issue> {
  if (!estActif({ statut: statutActuel })) {
    throw new Error(`Un problème ${statutActuel.toLowerCase()} ne se prépare plus : modifiez-le.`)
  }
  // Aucun badge = on retire le traitement prévu ; sinon, mêmes règles qu'à la
  // clôture (« Autre » exclusif, pas de doublon, pas d'inconnu).
  if (traitement.badges.length > 0) {
    const refus = erreurBadges(traitement.badges)
    if (refus) throw new Error(refus)
  }
  const refs = validerReferences(traitement.references ?? {}, traitement.badges)

  return updateIssue(itemId, {
    badgesCloture: [...traitement.badges],
    refFnct: traitement.badges.includes('FNC-T') ? (refs.fnct ?? '') : '',
    refNcs: traitement.badges.includes('NC-S') ? (refs.ncs ?? '') : '',
    commentaireCloture: traitement.commentaire.trim(),
  })
}

/**
 * Déclaration NC-S déposée (05/10) : le problème porte désormais le badge
 * NC-S — actif comme clos, le traitement qualité dit qu'une NC-S existe. Sa
 * référence (NC-S_2026-72) viendra plus tard, saisie ou cherchée.
 *
 * Relu avant écriture : seuls les badges changent, et à partir de ceux
 * enregistrés, pas de ceux d'une fiche ouverte depuis longtemps. « Autre »
 * est retiré (`badgesAvecNcs`). Rien n'est écrit si NC-S y est déjà.
 */
export async function ajouterBadgeNcs(itemId: string): Promise<Issue> {
  const actuel = mapItemToIssue(
    await graphGet<SharePointListItem<IssueFields>>(`${listPath()}/items/${itemId}?${ITEM_SELECT}`),
  )
  if (actuel.statut === ANNULE) throw new Error('Un problème annulé ne se déclare pas en NC-S.')
  const avant = actuel.badgesCloture ?? []
  if (avant.includes('NC-S')) return actuel
  return updateIssue(itemId, { badgesCloture: badgesAvecNcs(avant) })
}

/**
 * N° de NC-S retrouvé dans le registre de la qualité (06/10) — actif comme
 * clos, contrairement à `renseignerReferences`. Relu avant écriture : une
 * référence déjà saisie (à la main, ou par un collègue entre-temps) n'est
 * jamais écrasée par une autre.
 * @returns `ecrit` faux si le problème portait déjà ce N° (rien à tracer :
 *          la fiche et la recherche de fond peuvent le trouver en même temps).
 */
export async function enregistrerReferenceNcs(itemId: string, ref: string): Promise<{ issue: Issue; ecrit: boolean }> {
  const actuel = mapItemToIssue(
    await graphGet<SharePointListItem<IssueFields>>(`${listPath()}/items/${itemId}?${ITEM_SELECT}`),
  )
  if (actuel.statut === ANNULE) throw new Error('Un problème annulé ne reçoit pas de référence NC-S.')
  if (!estReferenceValide('NC-S', ref)) throw new Error(`Référence NC-S invalide : « ${ref} ».`)
  if (actuel.refNcs === ref) return { issue: actuel, ecrit: false }
  if (actuel.refNcs) throw new Error(`Ce problème a déjà la référence ${actuel.refNcs}.`)
  const issue = await updateIssue(itemId, {
    refNcs: ref,
    // Le badge accompagne toujours sa référence.
    ...(actuel.badgesCloture?.includes('NC-S') ? {} : { badgesCloture: badgesAvecNcs(actuel.badgesCloture ?? []) }),
  })
  return { issue, ecrit: true }
}

/**
 * Problème escaladé en FNC-T (chantier FNC-T, lot F2) : sa référence RefFNCT
 * et le badge FNC-T, actif comme clos. Relu avant écriture ; une référence
 * FNC-T déjà portée (une autre FNC-T) n'est jamais écrasée.
 * @returns `ecrit` faux si le problème portait déjà ce N°.
 */
export async function lierFnct(itemId: string, numero: string): Promise<{ issue: Issue; ecrit: boolean }> {
  const actuel = mapItemToIssue(
    await graphGet<SharePointListItem<IssueFields>>(`${listPath()}/items/${itemId}?${ITEM_SELECT}`),
  )
  if (actuel.statut === ANNULE) throw new Error('Un problème annulé ne s’escalade pas en FNC-T.')
  if (!estReferenceValide('FNC-T', numero)) throw new Error(`N° de FNC-T invalide : « ${numero} ».`)
  if (actuel.refFnct === numero) return { issue: actuel, ecrit: false }
  if (actuel.refFnct) throw new Error(`Ce problème a déjà la FNC-T ${actuel.refFnct}.`)
  const badges = actuel.badgesCloture ?? []
  const issue = await updateIssue(itemId, {
    refFnct: numero,
    ...(badges.includes('FNC-T') ? {} : { badgesCloture: basculerBadge(badges, 'FNC-T') }),
  })
  return { issue, ecrit: true }
}

/**
 * Renseigne, corrige ou retire le N° de plan d'un problème MECA.
 *
 * Réservé au métier MECA : c'est là que le plan de fabrication sert à
 * analyser le défaut. Possible à tout moment après la déclaration, clos
 * compris — le plan n'est souvent identifié qu'une fois le problème traité.
 * Refusé sur un annulé : il n'y a rien à analyser.
 *
 * @param saisie un plan par ligne ; chaîne vide = retirer
 */
export async function renseignerNumeroPlan(
  itemId: string,
  saisie: string,
  issue: Pick<Issue, 'metier' | 'statut'>,
): Promise<Issue> {
  if (issue.metier !== 'MECA') {
    throw new Error('Le N° de plan ne concerne que les problèmes MECA.')
  }
  if (issue.statut === ANNULE) {
    throw new Error('Un problème annulé ne se complète plus.')
  }
  return updateIssue(itemId, { numeroPlan: normaliserNumeroPlan(saisie) })
}

/**
 * Renseigne, corrige ou retire le schéma électrique d'un problème (Lot 13).
 *
 * ELEC / AUTOM seulement ; à tout moment, clos compris ; pas sur un annulé —
 * même règle que le N° de plan MECA. La saisie est mise au format ici
 * (`normaliserSaisieEDE`) : rien ne part hors format. Une requête ; la fiche
 * trace l'événement « Modification » ensuite.
 */
export async function renseignerDocumentationEDE(
  itemId: string,
  saisie: SaisieEDE,
  issue: Pick<Issue, 'metier' | 'statut'>,
): Promise<DocumentationEDE> {
  if (!concerneEDE(issue.metier)) {
    throw new Error('Le schéma électrique ne concerne que les problèmes ELEC et AUTOM.')
  }
  if (issue.statut === ANNULE) {
    throw new Error('Un problème annulé ne se complète plus.')
  }
  const doc = normaliserSaisieEDE(saisie)
  await updateIssue(itemId, doc)
  return doc
}

// ─── Édition (Lot 6 : coordinateur ; Lot 7 : administrateur) ───

export interface ResultatModification {
  issue: Issue
  /** Ce qui a changé, pour l'événement « Modification » de la timeline. */
  changements: Changement[]
  /** Noms des coordinateurs désormais assignés — seulement si le métier a changé. */
  suiviPar: string[] | null
}

/**
 * Enregistre l'édition d'un problème par son coordinateur ou par
 * l'administrateur (« édition totale » : équipement, données de fin, tous
 * statuts).
 *
 * Règles : `utils/modificationIssue.ts` — la fiche applique les mêmes pour
 * griser son bouton, l'API les applique à nouveau avant d'écrire.
 *
 * ─── Deux précautions propres à l'écriture ───
 *
 * 1. Relecture avant écriture. Deux coordinateurs MECA peuvent avoir la même
 *    fiche ouverte. Si l'un des champs modifiés (ou le statut) a changé
 *    depuis le chargement, on refuse plutôt que d'écraser — et plutôt que
 *    d'écrire dans la timeline un « avant » qui ne serait plus vrai. Une
 *    lecture de plus, sur un geste rare.
 *
 * 2. Changement de métier = réassignation des coordinateurs (décision 5,
 *    option B), dans la MÊME requête que le métier : pas d'état où le
 *    problème serait MECA et suivi par le coordinateur ELEC. Si aucun
 *    coordinateur du nouveau métier ne se résout, rien n'est écrit — les
 *    notifications partiraient sinon aux anciens.
 *
 * Seuls les champs qui changent sont écrits. La trace dans la timeline est à
 * la charge de l'appelant, comme pour les autres transitions.
 *
 * @param avant le problème tel qu'affiché au moment d'ouvrir l'édition
 * @param admin périmètre administrateur — décidé par l'appelant sur
 *              l'identité AFFICHÉE (en bascule de rôle, on édite comme la
 *              personne affichée)
 * @throws Error si la modification est refusée ou si le problème a changé
 *         entre-temps — dans tous les cas sans rien écrire.
 */
export async function modifierIssue(
  itemId: string,
  avant: Issue,
  saisie: SaisieModification,
  { admin }: { admin: boolean },
): Promise<ResultatModification> {
  const { apres, changements, refus } = analyserModification(avant, saisie, { admin })
  if (refus) throw new Error(refus)

  // 1. Relecture
  await verifierInchange(itemId, avant, changements.map((c) => c.champ))

  const partiel: Partial<Issue> = {}
  for (const c of changements) {
    // Types garantis par analyserModification (catégorie non nulle, motif
    // connu…) ; les références retirées valent '' — colonne texte vidée.
    Object.assign(partiel, { [c.champ]: apres[c.champ] })
  }
  const fields = versChampsSharePoint(partiel)

  // 2. Réassignation
  let suiviPar: string[] | null = null
  if (changements.some((c) => c.champ === 'metier' || c.champ === 'sujet')) {
    const coordinateurs = coordinateursDuProbleme({ metier: apres.metier, ...(apres.sujet ? { sujet: apres.sujet } : {}) })
    const lookups = await resolveUserLookupIds(coordinateurs.map((c) => c.upn))
    const resolus = coordinateurs.filter((c) => lookups.get(c.upn.toLowerCase()) != null)
    if (resolus.length === 0) {
      throw new Error(
        `Impossible d'identifier les coordinateurs ${apres.metier} dans SharePoint : ` +
        'le métier n\'a pas été changé.',
      )
    }
    fields['field_15LookupId@odata.type'] = 'Collection(Edm.Int32)'
    fields.field_15LookupId = resolus.map((c) => lookups.get(c.upn.toLowerCase()) as number)
    suiviPar = resolus.map((c) => c.nom)
  }

  const issue = await ecrireChamps(itemId, fields)
  return { issue, changements, suiviPar }
}

/**
 * Relit le problème et refuse si le statut, ou l'un des champs que l'on
 * s'apprête à écrire, a changé depuis que la fiche a été chargée.
 */
async function verifierInchange(
  itemId: string,
  avant: Issue,
  champs: readonly ChampModifiable[],
): Promise<void> {
  const actuel = mapItemToIssue(
    await graphGet<SharePointListItem<IssueFields>>(
      `${listPath()}/items/${itemId}?${ITEM_SELECT}`,
    ),
  )
  const eAvant = analyserModification(avant, saisieDepuis(avant), { admin: true }).apres
  const eActuel = analyserModification(actuel, saisieDepuis(actuel), { admin: true }).apres
  const bouge = champs.filter((c) => enTexte(eActuel, c) !== enTexte(eAvant, c))
  if (actuel.statut !== avant.statut || bouge.length > 0) {
    const quoi =
      actuel.statut !== avant.statut
        ? `statut passé à « ${actuel.statut} »`
        : bouge.map((c) => LIBELLES_CHAMPS[c].toLowerCase()).join(', ')
    throw new Error(
      `Ce problème a été modifié entre-temps (${quoi}). ` +
      'Fermez et rechargez la fiche : rien n\'a été enregistré.',
    )
  }
}

// ─── Réouverture (Lot 7, administrateur) ───

/**
 * Rouvre un problème clos ou annulé : il repasse `En cours` — ou `Ouvert`
 * s'il attend encore sa qualification (cf. statutReouverture).
 *
 * Les données de fin sont EFFACÉES de la fiche, en une requête avec le
 * statut : un problème rouvert qui garderait ses badges, son commentaire de
 * clôture ou son motif d'annulation se lirait comme encore terminé (badges
 * en tête de fiche, export, statistiques). Elles ne sont pas perdues : la
 * timeline les cite en entier (resumerReouverture), à la charge de l'appelant.
 *
 * Réservé à l'administrateur — garde à la charge de l'appelant, comme les
 * autres transitions. Un commentaire est obligatoire, comme pour clore ou
 * annuler : on doit pouvoir lire POURQUOI.
 *
 * @throws Error si le problème n'est pas terminé, si le commentaire est vide,
 *         ou si le statut a changé depuis le chargement — sans rien écrire.
 */
export async function rouvrirIssue(
  itemId: string,
  avant: Issue,
  commentaire: string,
): Promise<{ issue: Issue; nouveauStatut: Statut }> {
  if (!estTermine(avant)) {
    throw new Error('Seul un problème clos ou annulé se rouvre.')
  }
  if (!commentaire.trim()) {
    throw new Error('Un commentaire est obligatoire pour rouvrir un problème.')
  }
  await verifierInchange(itemId, avant, [])

  const nouveauStatut = statutReouverture(avant)
  const fields: Partial<IssueFields> = {
    field_14: nouveauStatut,
    // Effacement : null pour les colonnes date et choix, '' pour le texte,
    // tableau vide (annoté) pour le choix multiple.
    field_16: null,
    [COLONNES_AJOUTEES.dateAnnulation]: null,
    [COLONNES_AJOUTEES.motifAnnulation]: null,
    [CLE_TYPE_BADGES]: 'Collection(Edm.String)',
    [COLONNES_AJOUTEES.badgeCloture]: [],
    [COLONNES_AJOUTEES.commentaireCloture]: '',
    [COLONNES_AJOUTEES.refFnct]: '',
    [COLONNES_AJOUTEES.refNcs]: '',
  }
  const issue = await ecrireChamps(itemId, fields)
  return { issue, nouveauStatut }
}

// ─── Historique des corrections (23/10, lot 1) ───

interface VersionGraphProbleme {
  id: string
  lastModifiedDateTime?: string
  lastModifiedBy?: GraphIdentity
  fields?: Partial<IssueFields>
}

/** Une version telle que Graph la rend → les champs suivis (un champ absent = vide). */
function versionDepuis(date: string, f: Partial<IssueFields>, ecritPar?: GraphIdentity): VersionProbleme {
  // Ramené à la personne (compte secondaire ; appareil partagé : SaisiPar, qui
  // ne dit que qui a DÉCLARÉ — une correction faite plus tard sur la tablette
  // lui serait attribuée).
  const auteur = auteurCanonique(ecritPar?.user, undefined, undefined, f[COLONNES_AJOUTEES.saisiPar])
  const par = auteur ? nomComplet(auteur.displayName ?? auteur.email ?? '', auteur.email) : ''
  return {
    date,
    ...(par ? { par } : {}),
    champs: {
      titre: (f.field_1 ?? '').trim(),
      description: (f.field_10 ?? '').trim(),
      phase: f.field_5 ?? '',
      numeroPlan: normaliserNumeroPlan(f[COLONNES_AJOUTEES.numeroPlan] ?? '').split('\n').join(', '),
      ede: texteEde(f[COLONNES_AJOUTEES.numeroEDE], f[COLONNES_AJOUTEES.revEDE], f[COLONNES_AJOUTEES.pageEDE]),
    },
  }
}

/**
 * Les versions d'un problème (historique de SharePoint, activé sur
 * SCE_Problemes), lues pour l'historique des corrections de la fiche.
 * `$expand=fields` d'abord ; s'il ne rend pas les champs, version par version
 * (au plus 30). Une version illisible est écartée.
 */
export async function historiqueProbleme(itemId: string): Promise<VersionProbleme[]> {
  const base = `${listPath()}/items/${itemId}/versions`
  const reponse = await graphGet<{ value: VersionGraphProbleme[] }>(`${base}?$expand=fields`)
  const versions = await Promise.all(
    reponse.value.slice(0, 30).map(async (v): Promise<VersionProbleme | null> => {
      let champs = v.fields
      if (!champs) {
        try {
          champs = await graphGet<Partial<IssueFields>>(`${base}/${encodeURIComponent(v.id)}/fields`)
        } catch {
          return null
        }
      }
      return versionDepuis(v.lastModifiedDateTime ?? '', champs, v.lastModifiedBy)
    }),
  )
  return versions.filter((v): v is VersionProbleme => v !== null)
}
